/**
 * Tabs Component Modul
 * Spravuje funkčnost záložek s podporou přístupnosti a správou tříd typografie.
 * Zahrnuje hlášení chyb přes Sentry a automatické čištění event listenerů.
 */

import * as Sentry from '@sentry/browser';
import { updateTypographyClass } from '../../utils/updateTypographyClass';

// CSS třídy pro aktivní stavy
const ACTIVE_CLASS = 'tabs__item--active';
const PANEL_ACTIVE_CLASS = 'tabs__panel--active';
const HIDDEN_CLASS = 'tabs__item--hidden';
const TABS_HIDDEN_CLASS = 'tabs--hidden';

export const Tabs = (tabContainer: HTMLElement) => {
  try {
    // Načtení všech spouštěčů záložek a nastavení počátečního aktivního indexu
    const tabTriggers = Array.from(
      tabContainer.querySelectorAll('[data-tab-trigger]')
    ) as HTMLElement[];

    let activeIndex = parseInt(tabContainer.getAttribute('data-active-index') || '0', 10);

    /**
     * Kontrola, zda je panel prázdný a nebo chybí
     * Vrací true, pokud by mělo být skryto
     */
    const shouldHideTab = (trigger: HTMLElement): boolean => {
      const tabId = trigger.getAttribute('data-tab-id');
      if (!tabId) return true;

      const panel = tabContainer.querySelector(`[data-tab-panel="${tabId}"]`) as HTMLElement;
      if (!panel) return true;

      // Check if panel has any content (excluding whitespace)
      return !panel.textContent?.trim();
    };

    /**
     * Kontrola, zda jsou všechny taby skryté
     * Vrací true, pokud jsou všechny taby skryté
     */
    const areAllTabsHidden = (): boolean => {
      return tabTriggers.every((trigger) => trigger.classList.contains(HIDDEN_CLASS));
    };

    /**
     * Aktualizuje viditelnost celého kontejneru tabů
     */
    const updateTabsContainerVisibility = () => {
      const shouldHideContainer = areAllTabsHidden();
      tabContainer.classList.toggle(TABS_HIDDEN_CLASS, shouldHideContainer);
    };

    /**
     * Skryje prázdné taby a nastaví aktivní index, pokud je třeba
     */
    const hideEmptyTabs = () => {
      let hasAdjustedActiveIndex = false;

      tabTriggers.forEach((trigger, index) => {
        if (shouldHideTab(trigger)) {
          trigger.classList.add(HIDDEN_CLASS);
          // Pokud to byl aktivní tab, potřebujeme nastavit nový
          if (index === activeIndex && !hasAdjustedActiveIndex) {
            const nextVisibleIndex = tabTriggers.findIndex(
              (t, i) => i > index && !shouldHideTab(t)
            );
            if (nextVisibleIndex !== -1) {
              activeIndex = nextVisibleIndex;
            } else {
              const prevVisibleIndex = tabTriggers
                .slice(0, index)
                .findIndex((t) => !shouldHideTab(t));
              if (prevVisibleIndex !== -1) {
                activeIndex = prevVisibleIndex;
              }
            }
            hasAdjustedActiveIndex = true;
          }
        }
      });

      // Nastaví viditelnost konteineru po skrytí prázdných tabů
      updateTabsContainerVisibility();
    };

    /**
     * Nastaví aktivní stav vybrané záložky
     * Aktualizuje třídy, ARIA atributy a typografii
     * Spravuje viditelnost přidruženého panelu
     * Aktualizuje URL kotvu bez rolování
     */

    const setActiveTab = (index: number, updateUrl = false) => {
      tabTriggers.forEach((trigger, idx) => {
        // Skip hidden tabs
        if (trigger.classList.contains(HIDDEN_CLASS)) return;

        const isActive = idx === index;
        const tabId = trigger.getAttribute('data-tab-id');
        const tabAnchor = trigger.getAttribute('href')?.split('#')[1] || '';

        trigger.classList.toggle(ACTIVE_CLASS, isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');

        // Použití utility pro aktualizaci typografie
        updateTypographyClass(trigger, isActive);

        if (tabId) {
          const panel = tabContainer.querySelector(`[data-tab-panel="${tabId}"]`) as HTMLElement;
          if (panel) {
            panel.classList.toggle(PANEL_ACTIVE_CLASS, isActive);
            panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          }
        }

        // Aktualizace URL kotvy bez rolování pouze na základě explicitního požadavku
        if (isActive && updateUrl && tabAnchor) {
          history.replaceState(
            null,
            '',
            `${window.location.pathname}${window.location.search}#${tabAnchor}`
          );
        }
      });

      activeIndex = index;
    };

    /**
     * Obslužná funkce kliknutí na spouštěče záložek
     * Zabraňuje výchozímu chování a aktualizuje aktivní stav
     */
    const onTabClick = (index: number, event: Event) => {
      event.preventDefault();
      // Only activate if the tab isn't hidden
      if (!tabTriggers[index].classList.contains(HIDDEN_CLASS)) {
        setActiveTab(index, true); // Předat true pro aktualizaci URL
      }
    };

    /**
     * Najde a aktivuje záložku na základě URL kotvy
     * Vrací true, pokud byla záložka aktivována, jinak false
     */
    const selectTabFromUrlAnchor = (): boolean => {
      const currentAnchor = window.location.hash.substring(1);
      if (!currentAnchor) return false;

      const matchingTrigger = tabTriggers.find(
        (trigger) =>
          !trigger.classList.contains(HIDDEN_CLASS) &&
          trigger.getAttribute('href')?.split('#')[1] === currentAnchor
      );

      if (matchingTrigger) {
        const index = tabTriggers.indexOf(matchingTrigger);
        setActiveTab(index);
        return true;
      }

      return false;
    };

    /**
     * Posune zobrazení na první aktivní záložku, pokud je nalezena
     */
    const scrollToFirstActiveTab = () => {
      const activeTab = tabTriggers.find(
        (trigger) =>
          !trigger.classList.contains(HIDDEN_CLASS) && trigger.classList.contains(ACTIVE_CLASS)
      );

      if (activeTab) {
        tabContainer.scrollIntoView({
          behavior: 'smooth',
        });
      }
    };

    /**
     * Připojí event listeners ke všem spouštěčům záložek
     */
    const addEventListeners = () => {
      tabTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (event) => onTabClick(index, event));
      });
    };

    /**
     * Inicializuje komponentu záložek
     * Ověří existenci požadovaných elementů
     * Nastaví počáteční aktivní záložku a připojí události
     */
    const init = () => {
      if (tabTriggers.length === 0) {
        Sentry.captureException('Tabs: Missing tab triggers');
        return;
      }

      // Skryje prázdné taby před initem aktivního stavu
      hideEmptyTabs();

      // Pokusí se vybrat záložku podle URL kotvy
      const selectedFromAnchor = selectTabFromUrlAnchor();

      // Pokud nebyla záložka vybrána z kotvy, použije výchozí aktivní index
      if (!selectedFromAnchor) {
        setActiveTab(activeIndex);
      }

      addEventListeners();

      // Posune zobrazení na první aktivní záložku při inicializaci pokud byla zadána kotva
      if (selectedFromAnchor) {
        scrollToFirstActiveTab();
      }
    };

    init();

    // Vrátí funkci pro úklid, která odstraní event listeners
    return () => {
      tabTriggers.forEach((trigger, index) => {
        trigger.removeEventListener('click', (event) => onTabClick(index, event));
      });
    };
  } catch (err) {
    Sentry.captureException(err);
  }
};

export default Tabs;
